import { DATE_FORMAT } from "../constants/app-constant";
import {
  EASTERN_TIME_ZONE,
  OPCD_API_STATUS,
  OPCD_MODULE_SLUG,
  OPCD_RESPONSE_STATUS,
} from "../constants/opcd-constants";
import type {
  OpcdClinicalIndicator,
  OpcdClinicalReference,
  OpcdQueriesListResponse,
  OpcdQuery,
  OpcdQueryListRow,
  OpcdQueryOption,
  OpcdQueryResponse,
} from "../types/opcd-queries";
import { calculateAging, formatDate, formatTimeWithZoneAbbreviation } from ".";

const normalizeStatus = (status?: string): string =>
  (status ?? "").trim().toLowerCase().replace(/[\s-]+/g, "_");

const asModuleSlug = (value: unknown): string | undefined => {
  if (typeof value !== "string") return undefined;
  const slug = value.trim();
  return slug || undefined;
};

const normalizeOpcdQuery = (value: unknown): OpcdQuery | null => {
  if (!value || typeof value !== "object") return null;
  const query = value as OpcdQuery;
  return {
    ...query,
    moduleSlug: asModuleSlug(query.moduleSlug),
  };
};

export const isOpcdOutpatientCdiQuery = (
  moduleSlug?: string | null
): boolean => (moduleSlug ?? "").trim() === OPCD_MODULE_SLUG.OUTPATIENT_CDI;

export const getOpcdDocumentedHeading = (
  moduleSlug: string | undefined,
  codeType?: string
): string => {
  if (isOpcdOutpatientCdiQuery(moduleSlug)) {
    const normalized = (codeType ?? "").trim().toUpperCase().replace(/\s+/g, "");
    if (normalized === "CPT" || normalized === "EM" || normalized === "E/M") {
      return "Documented CPT code";
    }
    return "Documented Diagnosis";
  }
  return "Documented Condition/Finding";
};

export const formatOpcdClinicalIndicatorLine = (
  indicator: OpcdClinicalIndicator
): string => {
  const name = (indicator.name || indicator.testName || "").trim();
  const result = (indicator.result ?? "").trim();
  const date = (indicator.date ?? "").trim();
  const label = name || "Clinical finding";
  return `${label}${result ? ` - ${result}` : ""}${date ? ` (${date})` : ""}`;
};

export const joinOpcdReferenceTexts = (
  items: OpcdClinicalReference[] | undefined
): string =>
  (items ?? [])
    .map((item) => (item.text || item.name || "").trim())
    .filter(Boolean)
    .join("; ");

export type OpcdIndicatorGroup = {
  type: string;
  heading?: string;
  items: OpcdClinicalIndicator[];
};

const PRO_FEE_INDICATOR_ORDER = ["LIF", "SSV", "TM"] as const;
const PRO_FEE_INDICATOR_LABELS: Record<string, string> = {
  LIF: "Lab/Imaging Findings",
  SSV: "Signs and Symptoms/Vital",
  TM: "Treatment/Management",
};

export const groupOpcdProFeeIndicators = (
  indicators: OpcdClinicalIndicator[] | undefined
): OpcdIndicatorGroup[] => {
  const grouped = new Map<string, OpcdClinicalIndicator[]>();
  for (const indicator of indicators ?? []) {
    const type = (indicator.type ?? "").trim().toUpperCase() || "LIF";
    const list = grouped.get(type) ?? [];
    list.push(indicator);
    grouped.set(type, list);
  }
  const known = new Set<string>(PRO_FEE_INDICATOR_ORDER);
  const extraTypes = Array.from(grouped.keys()).filter(
    (type) => !known.has(type)
  );
  const types = [
    ...PRO_FEE_INDICATOR_ORDER.filter((type) => grouped.has(type)),
    ...extraTypes,
  ];
  return types.map((type) => ({
    type,
    heading: PRO_FEE_INDICATOR_LABELS[type] ?? type,
    items: grouped.get(type) ?? [],
  }));
};

export const groupOpcdCdiIndicators = (
  indicators: OpcdClinicalIndicator[] | undefined
): OpcdIndicatorGroup[] => {
  const groups: OpcdIndicatorGroup[] = [];
  const indexByType = new Map<string, OpcdIndicatorGroup>();
  for (const indicator of indicators ?? []) {
    const type = (indicator.type ?? "").trim().toUpperCase();
    const key = type || "_none";
    let group = indexByType.get(key);
    if (!group) {
      group = {
        type: key,
        heading: type === "TM" ? "Treatment/Management" : undefined,
        items: [],
      };
      indexByType.set(key, group);
      groups.push(group);
    }
    group.items.push(indicator);
  }
  return groups;
};

export const isOpcdQueryPending = (status?: string): boolean =>
  normalizeStatus(status) === normalizeStatus(OPCD_API_STATUS.PENDING);

export const isOpcdQueryCompleted = (
  status?: string,
  response?: OpcdQueryResponse | null
): boolean => {
  const normalized = normalizeStatus(status);
  return (
    normalized === normalizeStatus(OPCD_API_STATUS.COMPLETED) ||
    normalized === "provider_responded" ||
    normalized === "responded" ||
    normalizeStatus(response?.status) === OPCD_RESPONSE_STATUS.COMPLETED
  );
};

export const extractOpcdQueriesListResponse = (
  payload: unknown
): OpcdQueriesListResponse => {
  const empty: OpcdQueriesListResponse = {
    success: false,
    count: 0,
    queries: [],
  };
  if (!payload || typeof payload !== "object") return empty;

  const root = payload as Record<string, unknown>;
  if (Array.isArray(root.queries)) {
    const queries = root.queries
      .map(normalizeOpcdQuery)
      .filter((query): query is OpcdQuery => Boolean(query));
    return {
      success: Boolean(root.success),
      count: Number(root.count ?? queries.length),
      queries,
    };
  }

  if (root.data && typeof root.data === "object") {
    return extractOpcdQueriesListResponse(root.data);
  }

  return empty;
};

export const extractOpcdQueryDetail = (payload: unknown): OpcdQuery | null => {
  if (!payload || typeof payload !== "object") return null;
  const root = payload as Record<string, unknown>;

  if (root.query && typeof root.query === "object") {
    return normalizeOpcdQuery(root.query);
  }
  if (typeof root.queryId === "string") {
    return normalizeOpcdQuery(root);
  }
  if (root.data && typeof root.data === "object") {
    return extractOpcdQueryDetail(root.data);
  }
  return null;
};

export const transformOpcdQueriesToRows = (
  response: OpcdQueriesListResponse
): OpcdQueryListRow[] =>
  (response.queries ?? []).map((query) => {
    const dateSource = query.sentAt || query.createdAt;
    const completed = isOpcdQueryCompleted(query.status, query.response);
    return {
      id: query.queryId,
      queryId: query.queryId,
      patientName: query.request?.patient?.name || "-",
      patientMRN: query.request?.patient?.mrn || "-",
      status: completed
        ? OPCD_API_STATUS.COMPLETED
        : isOpcdQueryPending(query.status)
        ? "Open"
        : query.status || "-",
      statusApi: completed ? OPCD_API_STATUS.COMPLETED : query.status,
      ageing: completed ? "-" : calculateAging(dateSource),
      queryDate: formatDate(
        dateSource,
        DATE_FORMAT.MM_DD_YYYY,
        EASTERN_TIME_ZONE
      ),
      createdFormatTime: formatTimeWithZoneAbbreviation(
        dateSource,
        EASTERN_TIME_ZONE,
        "ET"
      ),
      moduleSlug: query.moduleSlug,
    };
  });

export const filterOpcdQueriesByStatus = (
  rows: OpcdQueryListRow[],
  statuses: string[]
): OpcdQueryListRow[] => {
  if (!statuses.length || statuses.length === OPCD_STATUS_VALUES.length) {
    return rows;
  }
  const normalized = statuses.map(normalizeStatus);
  return rows.filter((row) => normalized.includes(normalizeStatus(row.statusApi)));
};

const OPCD_STATUS_VALUES = [
  OPCD_API_STATUS.PENDING,
  OPCD_API_STATUS.COMPLETED,
];

export const filterOpcdQueriesBySearch = (
  rows: OpcdQueryListRow[],
  search: string
): OpcdQueryListRow[] => {
  const term = search.trim().toLowerCase();
  if (!term) return rows;
  return rows.filter(
    (row) =>
      row.patientName.toLowerCase().includes(term) ||
      row.patientMRN.toLowerCase().includes(term) ||
      row.queryId.toLowerCase().includes(term)
  );
};

export const isOpcdOtherOption = (option: OpcdQueryOption): boolean =>
  /\bother\b/i.test(option.label.trim());

export const extractOpcdResponseSelections = (
  response?: OpcdQueryResponse | null
): { selectedIds: string[]; otherText: string } => ({
  selectedIds: (response?.selectedOptions ?? []).map((option) =>
    String(option.id)
  ),
  otherText: response?.otherJustification ?? "",
});

export const buildOpcdSubmitOptions = (
  options: OpcdQueryOption[],
  selectedIds: string[],
  otherText: string
): OpcdQueryOption[] =>
  selectedIds.map((id) => {
    const option = options.find((item) => String(item.id) === id);
    if (!option) return { id, label: id };
    if (isOpcdOtherOption(option) && otherText.trim()) {
      return { ...option, label: `${option.label}: ${otherText.trim()}` };
    }
    return option;
  });
